import Phaser from 'phaser';
import { dailyLawn } from '../../gen/modes';
import type { Challenge } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { loadChallenge, onlineOn } from '../online';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { formatTime } from '../ui/widgets';
import { wrapLineCount } from '../ui/wrap';
import type { LevelSceneData } from './LevelScene';
import { wipeTo } from './WipeScene';
import { whoText } from '../nicknames';
import { dailyHasBoxes } from '../../meta/rulesVersion';

export interface ChallengeData {
  id: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "OCT 24" for a date key. */
export function shortDate(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  return d
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    .toUpperCase();
}

/**
 * A beat-my-time challenge, opened from its link (docs/ONLINE.md, lm-wxl.3): who sent it, their
 * time on which Daily, friends' times so far, and RACE, which plays that Daily against their ghost.
 */
export class ChallengeScene extends Phaser.Scene {
  private id = '';
  private challenge: Challenge | 'closed' | 'missing' | null = null;
  private loading = true;

  constructor() {
    super('Challenge');
  }

  init(data: ChallengeData): void {
    this.id = data.id;
    this.challenge = null;
    this.loading = true;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void this.fetch();
  }

  private async fetch(): Promise<void> {
    if (!onlineOn()) {
      this.loading = false;
      this.redraw();
      return;
    }
    const challenge = await loadChallenge(this.id);
    this.loading = false;
    this.challenge = challenge;
    this.redraw();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'CHALLENGE', 2);
    const backY = height - 30;
    bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => wipeTo(this, 'Menu'));
    const textW = width - 28;
    const say = (lines: { text: string; color: number }[]) => {
      let y = 34;
      const h = lines.reduce((t, l) => t + wrapLineCount(l.text, textW) * 10 + 4, 0);
      navyPanel(g, 4, y - 6, width - 8, h + 8);
      for (const l of lines) {
        label(this, 14, y, l.text, 1, l.color).setMaxWidth(textW);
        y += wrapLineCount(l.text, textW) * 10 + 4;
      }
      return y + 8;
    };

    if (!onlineOn()) {
      say([
        { text: 'Online scores are off.', color: UI.accent },
        { text: 'Turn them on in Settings, Online, to race challenges.', color: UI.dim },
      ]);
      bevelButton(this, 6, backY - 28, width - 12, 20, 'ONLINE SETTINGS', () =>
        wipeTo(this, 'Online'),
      );
      return;
    }
    const c = this.challenge;
    if (this.loading || c === null) {
      say([
        {
          text: this.loading ? 'Loading...' : "Can't reach the server right now. Try again later.",
          color: UI.dim,
        },
      ]);
      return;
    }
    if (c === 'closed' || c === 'missing') {
      say([
        {
          text: c === 'closed' ? 'This challenge has closed.' : 'This challenge is gone.',
          color: UI.accent,
        },
        { text: "Today's Daily is waiting, though.", color: UI.dim },
      ]);
      return;
    }

    const mine = c.from.player === saves().save.online.player?.id;
    const days = Math.max(0, Math.ceil((c.closes - Date.now()) / DAY_MS));
    const lines = [
      {
        text: mine ? 'Your challenge' : `${whoText(c.from.player, c.from.name)} challenged you`,
        color: UI.accent,
      },
      { text: `Daily of ${shortDate(c.date)}: ${formatTime(c.ticks, TICK_RATE)}`, color: UI.text },
      {
        text: days <= 1 ? 'Closes today.' : `Closes in ${days} days.`,
        color: UI.dim,
      },
    ];
    let y = say(lines);
    if (c.answers.length > 0) {
      label(this, 12, y, 'FRIENDS SO FAR', 1, UI.dim);
      y += 12;
      for (const a of c.answers.slice(0, 6)) {
        const beat = a.ticks < c.ticks;
        label(this, 12, y, whoText(a.player, a.name), 1, beat ? UI.good : UI.text);
        label(
          this,
          width - 12,
          y,
          formatTime(a.ticks, TICK_RATE),
          1,
          beat ? UI.good : UI.text,
        ).setOrigin(1, 0);
        y += 11;
      }
    }
    if (!mine) {
      if (dailyHasBoxes(c.run.rules)) {
        bevelButton(this, 6, backY - 34, width - 12, 28, 'RACE', () => this.race(c), true, 2);
      } else {
        // Sent from a version whose Daily had no item boxes (docs/DAILY_ITEMS.md): not this lawn.
        label(this, width / 2, backY - 30, 'Sent from an older version.', 1, UI.dim).setOrigin(
          0.5,
          0,
        );
        label(this, width / 2, backY - 20, 'Ask them to update to race.', 1, UI.dim).setOrigin(
          0.5,
          0,
        );
      }
    }
  }

  /** That day's Daily, with the sender's run alongside as a ghost. */
  private race(c: Challenge): void {
    const daily = dailyLawn(c.date);
    const data: LevelSceneData = {
      level: daily.def,
      mowerId: daily.loaner,
      daily: c.date,
      ghost: {
        run: c.run,
        name: c.from.name,
        player: c.from.player,
        ticks: c.ticks,
        kind: 'challenge',
      },
      challenge: c.id,
    };
    wipeTo(this, 'Level', data);
  }
}
